/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
// File modified from https://github.com/udecode/plate/blob/main/packages/resizable/src/components/Resizable.tsx
import {
  findNodePath,
  select,
  setNodes,
  TElement,
  useEditorRef,
  useElement,
} from 'lib-knowledge-document-structure/plate/common';
import {
  ResizeEvent,
  ResizeHandleProvider,
  ResizeLength,
  resizeLengthClamp,
} from 'lib-knowledge-document-structure/plate/resizable';
import {
  CSSProperties,
  forwardRef,
  HTMLAttributes,
  useCallback,
  useEffect,
  useRef,
} from 'react';

import { useResizableStore } from './useResizableStore';

export interface TResizableElement extends TElement {
  height?: number;
}

export interface ResizableOptions {
  maxHeight?: ResizeLength;

  minHeight?: ResizeLength;
  readOnly?: boolean;
}

export const useResizableState = ({
  // align = 'center',
  maxHeight = '100%',
  minHeight = 92,
}: ResizableOptions = {}) => {
  const element = useElement<TResizableElement>();
  const editor = useEditorRef();

  const nodeHeight = element?.height ?? '100%';

  const [height, setHeight] = useResizableStore().use.height();

  const setNodeHeight = useCallback(
    (h: number) => {
      const path = findNodePath(editor, element!);

      if (!path) return;
      if (h === nodeHeight) {
        // Focus the node if not resized
        select(editor, path);
      } else {
        setNodes<TResizableElement>(editor, { height: h }, { at: path });
      }
    },
    [editor, element, nodeHeight],
  );

  useEffect(() => {
    setHeight(nodeHeight);
  }, [nodeHeight, setHeight]);

  return {
    maxHeight,
    minHeight,
    setNodeHeight,
    setHeight,
    height,
  };
};

export const useResizable = ({
  // align,
  maxHeight,
  minHeight,
  setNodeHeight,
  setHeight,
  height,
}: ReturnType<typeof useResizableState>) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  return {
    context: {
      onResize: useCallback(
        ({ delta, direction, finished, initialSize }: ResizeEvent) => {
          const wrapperStaticWidth = wrapperRef.current!.offsetWidth;
          // const deltaFactor =
          //   (align === 'center' ? 2 : 1) * (direction === 'left' ? -1 : 1);
          const deltaFactor = direction === 'top' ? -1 : 1;

          const newHeight = resizeLengthClamp(
            initialSize + delta * deltaFactor,
            wrapperStaticWidth,
            {
              max: maxHeight,
              min: minHeight,
            },
          );

          if (finished) {
            setNodeHeight(newHeight);
          } else {
            setHeight(newHeight);
          }
        },
        [maxHeight, minHeight, setHeight, setNodeHeight],
      ),
    },
    props: {
      style: {
        maxHeight,
        minHeight,
        position: 'relative',
        height,
      } as CSSProperties,
    },
    wrapperProps: {
      style: {
        position: 'relative',
      } as CSSProperties,
    },
    wrapperRef,
  };
};

const Resizable = forwardRef<
  HTMLDivElement,
  {
    options: ResizableOptions;
  } & HTMLAttributes<HTMLDivElement>
>(({ children, options, ...rest }, ref) => {
  const state = useResizableState(options);
  const { context, props, wrapperProps, wrapperRef } = useResizable(state);

  return (
    <div ref={wrapperRef} {...wrapperProps}>
      <div ref={ref} {...props} {...rest}>
        <ResizeHandleProvider onResize={context.onResize}>
          {children}
        </ResizeHandleProvider>
      </div>
    </div>
  );
});
Resizable.displayName = 'Resizable';

export { Resizable };
